// barkEngine — the Tier A ambient "residents murmur as you pass" layer. Each
// resident has a small pool of pre-rendered voice clips (scripts/build_barks.js
// → public/barks/<slug>/…, manifest public/barks/<slug>.json). When the player
// enters a resident's MID zone (Npc → onTier), we play one eligible clip in the
// artist's own voice, dipping the station gently under it.
//
// Guards keep it from becoming a wall of noise: ONE bark plays at a time
// (global lock + cooldown), each resident has its own cooldown, and a resident
// never repeats its last line back-to-back. Manifests load lazily on first need.

import { audioEngine } from './audioEngine';

const GLOBAL_COOLDOWN_MS = 2500;   // min gap between ANY two barks (crowd ≠ chorus)
const SLUG_COOLDOWN_MS = 7000;     // a given resident won't bark again this soon
                                   // (hover is deliberate → shorter than a walk-by)
const CLIP_VOLUME = 1.0;

const manifests = new Map();       // slug → { lines: [...] } | null (miss)
const loading = new Map();         // slug → Promise (dedupe concurrent loads)
const lastIdx = new Map();         // slug → last played line index (no-repeat)
const slugNextOk = new Map();      // slug → earliest ts it may bark again
let globalNextOk = 0;
let current = null;                // the Audio element currently sounding

async function loadManifest(slug) {
  if (manifests.has(slug)) return manifests.get(slug);
  if (loading.has(slug)) return loading.get(slug);
  const p = fetch(`/barks/${slug}.json`, { cache: 'force-cache' })
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => { const v = m && Array.isArray(m.lines) && m.lines.length ? m : null; manifests.set(slug, v); return v; })
    .catch(() => { manifests.set(slug, null); return null; })
    .finally(() => loading.delete(slug));
  loading.set(slug, p);
  return p;
}

// Pick a clip for `slot`, avoiding the resident's immediately-previous line.
function pickLine(manifest, slug, slot) {
  const pool = manifest.lines.filter((l) => l.slot === slot);
  if (!pool.length) return null;
  if (pool.length === 1) return pool[0];
  const prev = lastIdx.get(slug);
  let choice;
  do { choice = pool[Math.floor(Math.random() * pool.length)]; } while (pool.length > 1 && choice.id === prev);
  return choice;
}

/**
 * Try to play a bark for `slug` in `slot`. Silently no-ops if a bark is already
 * sounding, either cooldown is active, or the resident has no clips. Returns
 * true if a clip started.
 */
export async function maybeBark(slug, slot = 'greeting_passerby') {
  const now = Date.now();
  if (current) return false;                       // one at a time
  if (now < globalNextOk) return false;            // global cooldown
  if (now < (slugNextOk.get(slug) || 0)) return false; // per-resident cooldown

  const manifest = await loadManifest(slug);
  if (!manifest) return false;
  // A concurrent call may have started a bark while we awaited the manifest.
  if (current || Date.now() < globalNextOk) return false;

  const line = pickLine(manifest, slug, slot);
  if (!line) return false;

  const audio = new Audio(`/barks/${slug}/${line.file}`);
  audio.volume = CLIP_VOLUME;
  const done = () => {
    if (current === audio) { current = null; audioEngine.dip(false); }
  };
  audio.addEventListener('ended', done);
  audio.addEventListener('error', done);

  current = audio;
  lastIdx.set(slug, line.id);
  const stamp = Date.now();
  slugNextOk.set(slug, stamp + SLUG_COOLDOWN_MS);
  globalNextOk = stamp + GLOBAL_COOLDOWN_MS;

  audioEngine.dip(true);
  audio.play().catch(() => done());               // autoplay blocked → release the dip
  return true;
}
